import BuildIcon from "@mui/icons-material/Build";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AgentThreadToolResultContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  displayToolName,
  isClientToolName,
} from "@/shared/domain/agentThread/clientTools/ClientTools";
import { describeToolCall } from "@/shared/domain/agentThread/describeToolCall";

import styles from "./AssistantToolResultContent.module.css";

const MS_TO_SECONDS_THRESHOLD = 100;

interface AssistantToolResultContentProps {
  toolResult: AgentThreadToolResultContent;
  /** The tool_use's proposed input, when the caller has the pair in hand —
   * lets `describeToolCall` label generic entity-access calls specifically
   * (e.g. `get_dataset` instead of `get_entity`). A tool_result block alone
   * does not carry it, so ungrouped rows typically render without it and
   * `describeToolCall` falls back to the real tool name. */
  toolInput?: Record<string, unknown>;
}

/**
 * A single ungrouped "Used Tool ..." row.
 *
 * Labels through the same `describeToolCall(displayToolName(...))` path as
 * `ToolUseGroup`, so a tool renders under the same name whether or not the
 * grouper managed to pair it: client tools drop their `client_` namespace
 * prefix and gain the laptop icon + "Client Tool" wording, and generic
 * entity-access calls take their specific name when the input is available.
 */
const AssistantToolResultContent = ({
  toolResult,
  toolInput,
}: AssistantToolResultContentProps) => {
  const formattedTime =
    toolResult.runtime_ms < MS_TO_SECONDS_THRESHOLD
      ? `${toolResult.runtime_ms}ms`
      : `${(toolResult.runtime_ms / 1000).toFixed(2)}s`;

  const isClient = isClientToolName(toolResult.tool_name);
  const Icon = isClient ? LaptopMacIcon : BuildIcon;
  const displayName = describeToolCall(
    displayToolName(toolResult.tool_name),
    toolInput,
  );

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.messageContainer}>
        <Icon className={isClient ? styles.clientToolIcon : styles.buildIcon} />
        <RobotoTypography className={styles.text}>
          Used {isClient ? "Client " : ""}Tool <b>{displayName}</b> (
          {formattedTime})
        </RobotoTypography>
      </div>
    </div>
  );
};

export { AssistantToolResultContent };
